import { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "@monolith-forensics/monolith-ui";
import { useSnackbar } from "notistack";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { File } from "@/Features/Files/files.types";
import { useDeleteStoragePhoto } from "@/Features/StoragePhotos";

export const DeleteStoragePhotoModal: React.FC<{
  show: boolean;
  storageId: number;
  storageUuid: string;
  photo: File;
  onClose?: () => void;
  onSubmit?: () => void;
}> = ({ show, storageId, storageUuid, photo, onClose, onSubmit }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: deleteStoragePhoto } = useDeleteStoragePhoto();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = () => {
    setSubmitting(true);
    deleteStoragePhoto(
      {
        storage_id: storageId,
        storage_uuid: storageUuid,
        photo_uuid: photo.uuid,
      },
      {
        onSuccess: () => {
          setSubmitting(false);
          enqueueSnackbar("Storage Photo Deleted.", { variant: "success" });
          onSubmit?.();
        },
        // The data layer rejects { success: false } envelopes, so failed
        // deletes land here; the axios interceptor already toasted HTTP errors.
        onError: (error) => {
          setSubmitting(false);
          console.error(error);
        },
      }
    );
  };

  const handleCancel = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [submitting, onClose]);

  // The base Modal does not dismiss on escape, so handle it here.
  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleCancel();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, handleCancel]);

  return (
    <Modal open={show} onClose={handleCancel} showCloseButton={true}>
      <Modal.Title>Delete Photo</Modal.Title>
      <div style={{ fontWeight: 600, margin: "20px 0px", width: "350px" }}>
        Are you sure you want to delete this photo?
      </div>
      <div style={{ fontWeight: 600, marginBottom: "20px", width: "350px" }}>
        "{photo.name}"
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="error"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Delete
        </Button>
      </ButtonMenu>
    </Modal>
  );
};
